// Timezone-aware "today". The app stores an optional IANA timezone in settings;
// when unset we use the browser's zone. Computing dates in UTC (toISOString)
// rolls the day over in the evening for western zones, so all "today"/current-
// month logic must go through here.

let overrideTz: string | null = null

export function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function setAppTimezone(tz: string | null | undefined) {
  overrideTz = tz && tz.trim() ? tz : null
}

export function appTimezone(): string {
  return overrideTz ?? browserTz()
}

// 'YYYY-MM-DD' for a given instant in a given zone (en-CA formats as ISO date).
export function dateInTz(tz: string, d: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  } catch {
    return d.toISOString().slice(0, 10)
  }
}

export function appToday(): string {
  return dateInTz(appTimezone())
}

// 'HH:MM' (24h) for an instant in a zone.
export function timeInTz(tz: string, d: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d)
  } catch {
    return d.toISOString().slice(11, 16)
  }
}

// ISO 'YYYY-MM-DD' -> 'MM/DD/YY' (month-first, 2-digit year) for display.
export function mmddyy(iso?: string | null): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${m}/${d}/${y.slice(2)}` : iso
}

// 'YYYY-MM' for the current month in the app zone.
export function appThisMonth(): string {
  return appToday().slice(0, 7)
}

// List of IANA zones for the settings picker (all supported, else a small set).
export function timezoneOptions(): string[] {
  const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
  if (typeof anyIntl.supportedValuesOf === 'function') {
    try {
      return anyIntl.supportedValuesOf('timeZone')
    } catch {
      /* fall through */
    }
  }
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
    'Europe/London',
    'Europe/Paris',
  ]
}
